import { createFileRoute, Link, Navigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useOpeningBalanceAuditLogs } from '@/api/audit-logs'
import { useIdempotencyKey } from '@/api/idempotency'
import { locationLabel, useOpeningBalance, useUpdateOpeningBalanceNote, useVoidOpeningBalance } from '@/api/opening-balances'
import type { OpeningBalanceLine } from '@/api/opening-balances'
import { poolLabel } from '@/api/pools'
import { useWorkspace } from '@/api/workspace'
import { correctionErrorMessage } from '@/components/corrections/error-message'
import { NoteCard } from '@/components/corrections/note-card'
import type { NoteSave } from '@/components/corrections/note-card'
import { DependentRecords } from '@/components/corrections/dependent-records'
import { VoidDialog } from '@/components/corrections/void-dialog'
import { VoidedBadge, VoidedNotice } from '@/components/corrections/voided'
import { voidedClass } from '@/components/corrections/voided-record'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { formatMoney, formatQty } from '@/components/materials/pool-format'
import { formatBusinessDate } from '@/components/opening-balances/format'
import { RecordHistory } from '@/components/record-history/record-history'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

// One Opening balance (UI-SCREENS I-13), Owner only: Pool lines (materials), WIP part lines
// (#139) or Variant lines at Home or a Sales Channel (qty only, 05e #173). A posted record is corrected by voiding it (with a reason), which frees its stock items
// for a corrected document; only its note stays editable (#68). A WIP void is refused when the
// parts were already used, listing the records to void first. Its History section (#69) is shown
// to the Owner, the only one who reaches the screen.
export const Route = createFileRoute('/w/$slug/settings/opening-balances/$openingBalanceId/')({
  component: OpeningBalanceDetail,
  staticData: { breadcrumb: 'settings.openingBalances.detail.title' },
})

function OpeningBalanceDetail() {
  const { t } = useTranslation()
  const { slug, openingBalanceId } = Route.useParams()
  const id = Number(openingBalanceId)
  const workspace = useWorkspace(slug)
  const document = useOpeningBalance(slug, id)
  const isOwner = workspace.data?.role === 'owner'
  const history = useOpeningBalanceAuditLogs(slug, id, { enabled: isOwner })
  const voidDocument = useVoidOpeningBalance()
  const updateNote = useUpdateOpeningBalanceNote()
  const idempotency = useIdempotencyKey()

  if (!isOwner) return <Navigate to="/w/$slug" params={{ slug }} replace />
  if (document.isPending) return <LoadingSkeleton variant="page" />
  if (document.isError || !document.data) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {t('errors.generic')}
      </p>
    )
  }

  const record = document.data
  const voided = Boolean(record.voided_at)

  // One Idempotency-Key per submission; the void is a transition, so it carries one.
  const onVoid = (reason: string, done: () => void) => {
    const body = { void: { reason } }
    voidDocument.mutate(
      { params: { path: { slug, opening_balance_id: id }, header: idempotency.headerFor(body) }, body },
      { onSuccess: done, onSettled: (_data, error) => idempotency.settle(error) },
    )
  }

  const onSaveNote = (note: string | null, save: NoteSave) => {
    updateNote.mutate(
      { params: { path: { slug, id } }, body: { opening_balance: { note, lock_version: save.lockVersion } } },
      { onSuccess: save.done, onSettled: (_data, error) => save.settle(error) },
    )
  }

  return (
    <section className={`flex max-w-2xl flex-col gap-4 ${voidedClass(voided)}`}>
      <Link to="/w/$slug/settings/opening-balances" params={{ slug }} className="text-sm underline-offset-4 hover:underline">
        {t('settings.openingBalances.back')}
      </Link>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-xl font-semibold">{formatBusinessDate(record.occurred_on)}</h1>
        <div className="flex items-center gap-3">
          <VoidedBadge record={record} />
          {record.can.void ? (
            <VoidDialog
              onVoid={onVoid}
              pending={voidDocument.isPending}
              errorMessage={voidDocument.isError ? correctionErrorMessage(t, voidDocument.error) : ''}
              blocked={voidDocument.isError ? <DependentRecords slug={slug} error={voidDocument.error} /> : null}
              onReset={voidDocument.reset}
            />
          ) : null}
        </div>
      </div>

      <Card>
        <CardContent className="flex flex-col gap-3 py-4">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-3">
            <Figure label={t('settings.openingBalances.columns.location')} value={locationLabel(t, record)} />
            {/* A WIP or Finished Goods document is qty only: no value at all (null, #139, #173). */}
            {record.total_value === null ? null : (
              <Figure
                label={t('settings.openingBalances.columns.totalValue')}
                value={`Rp ${formatMoney(record.total_value)}`}
              />
            )}
            <Figure
              label={t('settings.openingBalances.columns.createdBy')}
              value={document.data.created_by ? (document.data.created_by.name ?? document.data.created_by.email) : '—'}
            />
          </dl>
          <VoidedNotice record={record} />
        </CardContent>
      </Card>

      <NoteCard
        note={record.note}
        lockVersion={record.lock_version}
        canEdit={record.can.update_note}
        pending={updateNote.isPending}
        errorMessage={updateNote.isError ? correctionErrorMessage(t, updateNote.error) : ''}
        onSave={onSaveNote}
        onReset={updateNote.reset}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('settings.openingBalances.form.lines')}</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="flex flex-col divide-y">
            {document.data.lines.map((line) => (
              <li key={line.id} className="py-3 first:pt-0">
                <LineRow line={line} />
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <RecordHistory entries={history.data} isPending={history.isPending} isError={history.isError} />
    </section>
  )
}

function LineRow({ line }: { line: OpeningBalanceLine }) {
  const { t } = useTranslation()

  if (line.wip_part) return <WipLineRow line={line} wipPart={line.wip_part} />
  if (line.variant) {
    return (
      <div className="flex flex-col gap-1">
        <span className="font-medium">{`${line.variant.sku} · ${line.variant.name}`}</span>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <Figure label={t('settings.openingBalances.variant.qty')} value={formatQty(line.qty)} />
        </dl>
      </div>
    )
  }
  if (!line.pool) return null

  return (
    <div className="flex flex-col gap-1">
      <span className="font-medium">{poolLabel(line.pool)}</span>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-3">
        <Figure label={t('materials.columns.qty')} value={`${formatQty(line.qty)} ${line.pool.unit}`} />
        <Figure
          label={t('materials.columns.costPerUnit')}
          value={`Rp ${formatMoney(line.cost_per_unit ?? 0)}`}
        />
        <Figure
          label={t('settings.openingBalances.form.lineValue')}
          value={`Rp ${formatMoney(line.total_value ?? 0)}`}
        />
      </dl>
    </div>
  )
}

// A WIP part: its Model, the Pool of each color slot and the qty (no value, #139).
function WipLineRow({
  line,
  wipPart,
}: {
  line: OpeningBalanceLine
  wipPart: NonNullable<OpeningBalanceLine['wip_part']>
}) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col gap-1">
      <span className="font-medium">{wipPart.model.name}</span>
      <dl className="grid grid-cols-1 gap-x-4 gap-y-1 text-sm sm:grid-cols-2">
        {wipPart.slots.map((slot) => (
          <Figure key={slot.model_slot_id} label={slot.label} value={slot.pool.label} />
        ))}
        <Figure label={t('settings.openingBalances.wip.qty')} value={formatQty(line.qty)} />
      </dl>
    </div>
  )
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <dt className="text-muted-foreground">{label}</dt>
      <dd>{value}</dd>
    </div>
  )
}
